/**
 * Single source of truth for story-video upload validation limits.
 *
 * Scope: the story-video upload path only (single `DragAndDrop` +
 * `MultiFileUploadConcat`). Other upload surfaces (B-roll, artifacts, logos,
 * fonts, avatars) intentionally keep their own limits — see PLAT-10885.
 */

/** Accepted video container MIME types (picker hint + allowlist). */
export const ACCEPTED_VIDEO_MIME_TYPES: readonly string[] = [
  'video/mp4',
  'video/quicktime',
  'video/webm',
];

/**
 * Accepted lowercase file extensions → normalized container family.
 * `.mov`/`.qt` are QuickTime; `.mp4`/`.m4v` are ISO-BMFF/MP4; `.webm` is
 * Matroska/EBML.
 */
export const ACCEPTED_VIDEO_EXTENSIONS: Record<
  string,
  'mp4' | 'quicktime' | 'webm'
> = {
  mp4: 'mp4',
  m4v: 'mp4',
  mov: 'quicktime',
  qt: 'quicktime',
  webm: 'webm',
};

/** Max accepted source resolution — 4K UHD. Enforced in the container tier (P1). */
export const MAX_VIDEO_DIMENSION = { width: 3840, height: 2160 };

/**
 * Story-video size caps (GB), selected by LaunchDarkly flag. Single source for
 * both the hook (`useStoryCreator`) and the concat component so they can't
 * drift apart (they previously hardcoded 2.7 and 2 independently).
 */
export const STORY_VIDEO_SIZE_GB = {
  default: 2.7,
  large: 5,
  noLimit: 100,
};

export const gbToBytes = (gb: number): number => gb * 1024 * 1024 * 1024;

/**
 * Max total upload size eligible for audio enhancement (Auphonic). The limit
 * is our ffmpeg mux Lambda's memory ceiling when muxing the enhanced audio
 * back onto the video — not Auphonic's own limit (PLAT-11103).
 */
export const AUDIO_ENHANCEMENT_MAX_SIZE_GB = 8;

/** True when the selected files' total size is too large for audio enhancement. */
export const exceedsAudioEnhancementLimit = (totalBytes: number): boolean =>
  totalBytes > gbToBytes(AUDIO_ENHANCEMENT_MAX_SIZE_GB);
